// ContactDetails.tsx
import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { Contact, Interaction, InteractionType } from "../../models";
import InteractionTable from "../Contacts/InteractionTable";

interface ContactDetailsProps {
  contact: Contact;
  onUpdate: (contact: Contact) => void;
  onDelete: (contactId: string) => void;
}

const interactionTypes: InteractionType[] = [
  "cards",
  "meal",
  "visit",
  "text",
  "service",
  "other",
];

const ContactDetails: React.FC<ContactDetailsProps> = ({
  contact,
  onUpdate,
  onDelete,
}) => {
  // Local state for contact edits.
  const [editableContact, setEditableContact] = useState(contact);

  // Dialog state for adding a new interaction.
  const [isInteractionDialogOpen, setIsInteractionDialogOpen] =
    useState(false);
  const [newInteractionType, setNewInteractionType] =
    useState<InteractionType>("cards");
  const [newInteractionNotes, setNewInteractionNotes] = useState("");
  const [newInteractionDate, setNewInteractionDate] =
    useState<Dayjs | null>(dayjs());

  // Handle changes for contact fields.
  const handleContactChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setEditableContact((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Save updated contact.
  const handleSaveContact = () => {
    onUpdate(editableContact);
  };

  // Delete contact.
  const handleDeleteContact = () => {
    onDelete(editableContact.id);
  };

  // Add a new interaction.
  const handleAddInteraction = () => {
    if (newInteractionDate) {
      const interaction: Interaction = {
        date: newInteractionDate.toDate(),
        type: newInteractionType,
        notes: newInteractionNotes,
      };
      const updatedContact = {
        ...editableContact,
        interactions: [...editableContact.interactions, interaction],
      };
      setEditableContact(updatedContact);
      onUpdate(updatedContact);
      // Reset fields.
      setNewInteractionType("cards");
      setNewInteractionNotes("");
      setNewInteractionDate(dayjs());
      setIsInteractionDialogOpen(false);
    }
  };

  // Delete an interaction.
  const handleDeleteInteraction = (interactionToDelete: Interaction) => {
    const updatedInteractions = editableContact.interactions.filter(
      (interaction) => interaction !== interactionToDelete
    );
    const updatedContact = {
      ...editableContact,
      interactions: updatedInteractions,
    };
    setEditableContact(updatedContact);
    onUpdate(updatedContact);
  };

  return (
    <Box className="space-y-4">
      {/* Contact Details Card */}
      <Card className="shadow-md">
        <CardContent>
          <Typography variant="h5" className="mb-4">
            Contact Details
          </Typography>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <TextField
              label="Name"
              name="name"
              value={editableContact.name}
              onChange={handleContactChange}
              fullWidth
            />
            <TextField
              label="Phone Number"
              name="phoneNumber"
              value={editableContact.phoneNumber}
              onChange={handleContactChange}
              fullWidth
            />
            <TextField
              label="Email"
              name="email"
              value={editableContact.email}
              onChange={handleContactChange}
              fullWidth
            />
            <TextField
              label="Address"
              name="address"
              value={editableContact.address}
              onChange={handleContactChange}
              fullWidth
            />
            <TextField
              label="How did we meet?"
              name="howDidWeMeet"
              value={editableContact.howDidWeMeet}
              onChange={handleContactChange}
              fullWidth
            />
            <TextField
              label="Point of Contact"
              name="pointOfContact"
              value={editableContact.pointOfContact}
              onChange={handleContactChange}
              fullWidth
            />
            <Box className="flex items-center space-x-2">
              <input
                type="checkbox"
                name="bibleStudy"
                checked={editableContact.bibleStudy}
                onChange={handleContactChange}
                id="bibleStudy"
              />
              <label htmlFor="bibleStudy">Bible Study</label>
            </Box>
            <Box className="flex items-center space-x-2">
              <input
                type="checkbox"
                name="baptism"
                checked={editableContact.baptism}
                onChange={handleContactChange}
                id="baptism"
              />
              <label htmlFor="baptism">Baptism</label>
            </Box>
          </div>
          <div className="flex space-x-4 mt-4">
            <Button variant="contained" onClick={handleSaveContact}>
              Save Changes
            </Button>
            <Button variant="outlined" color="error" onClick={handleDeleteContact}>
              Delete Contact
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Interactions Card */}
      <Card className="shadow-md">
        <CardContent>
          <div className="flex justify-between items-center mb-4">
            <Typography variant="h5">Interactions</Typography>
            <Button
              variant="contained"
              onClick={() => setIsInteractionDialogOpen(true)}
            >
              Add Interaction
            </Button>
          </div>
          <InteractionTable
            interactions={editableContact.interactions}
            onDeleteInteraction={handleDeleteInteraction}
          />
        </CardContent>
      </Card>

      {/* Dialog for Adding a New Interaction */}
      <Dialog
        open={isInteractionDialogOpen}
        onClose={() => setIsInteractionDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add New Interaction</DialogTitle>
        <DialogContent className="space-y-4">
          {/* Use native date input */}
          <TextField
            label="Interaction Date"
            type="date"
            value={
              newInteractionDate
                ? newInteractionDate.toISOString().split("T")[0]
                : ""
            }
            onChange={(e) => setNewInteractionDate(dayjs(e.target.value))}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel id="interaction-type-label">Type</InputLabel>
            <Select
              labelId="interaction-type-label"
              value={newInteractionType}
              label="Type"
              onChange={(e) =>
                setNewInteractionType(e.target.value as InteractionType)
              }
            >
              {interactionTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Notes"
            value={newInteractionNotes}
            onChange={(e) => setNewInteractionNotes(e.target.value)}
            multiline
            rows={3}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsInteractionDialogOpen(false)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleAddInteraction}>
            Add Interaction
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContactDetails;
